import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, PenTool, Layers } from "lucide-react";
import { useCatalog, formatUSD } from "@/lib/CatalogProvider";
import SectionGlow from "@/components/site/SectionGlow";
import Reveal from "@/components/site/Reveal";

/**
 * Icon3D — a tactile, pseudo-3D badge that gives each service track an
 * immersive object to anchor it. Built from layered planes on a `preserve-3d`
 * stage (soft glow + back plate + lit face) with a gentle vertical float.
 * Float is disabled under reduced-motion; the object stays fully rendered.
 */
const Icon3D = ({ kind, reduce, accent }) => {
    const Icon = kind === "logo" ? PenTool : Layers;
    const spring = { duration: 6, repeat: Infinity, ease: "easeInOut" };
    return (
        <div
            className="pointer-events-none absolute right-5 top-5 z-[3] sm:right-6 sm:top-6"
            style={{
                perspective: "640px",
                ...(accent
                    ? {
                          "--pg-accent": accent,
                          "--pg-surface": `color-mix(in srgb, ${accent} 26%, #060913)`,
                          "--pg-surface-2": `color-mix(in srgb, ${accent} 36%, #0a0f1e)`,
                          "--pg-border-strong": `color-mix(in srgb, ${accent} 46%, #1a2340)`,
                      }
                    : {}),
            }}
            aria-hidden
        >
            <motion.div
                style={{ transformStyle: "preserve-3d" }}
                animate={reduce ? undefined : { y: [0, -7, 0] }}
                transition={reduce ? undefined : { duration: 4.8, repeat: Infinity, ease: "easeInOut" }}
            >
                <div style={{ transform: "rotateX(16deg)", transformStyle: "preserve-3d" }}>
                    {/* Whole chip turns while its planes slowly deconstruct + rebuild */}
                    <motion.div
                        className="relative h-16 w-16 sm:h-[72px] sm:w-[72px]"
                        style={{ transformStyle: "preserve-3d" }}
                        animate={reduce ? { rotateY: -22 } : { rotateY: [-26, -8, -26] }}
                        transition={reduce ? undefined : { duration: 7.5, repeat: Infinity, ease: "easeInOut" }}
                    >
                        {/* soft ground glow */}
                        <motion.span
                            className="absolute -inset-3 rounded-[24px]"
                            style={{
                                background:
                                    "radial-gradient(closest-side, color-mix(in srgb, var(--pg-accent) 42%, transparent), transparent 76%)",
                                filter: "blur(9px)",
                            }}
                            animate={reduce ? { z: -34, opacity: 0.7 } : { z: [-34, -62, -34], opacity: [0.5, 0.95, 0.5] }}
                            transition={reduce ? undefined : spring}
                        />
                        {/* back plate — slides furthest back on deconstruct */}
                        <motion.span
                            className="absolute inset-0 rounded-[18px]"
                            style={{
                                background: "color-mix(in srgb, var(--pg-accent) 24%, var(--pg-surface))",
                                border: "1px solid color-mix(in srgb, var(--pg-accent) 34%, var(--pg-border-strong))",
                            }}
                            animate={reduce ? { z: -14, y: 0 } : { z: [-14, -54, -14], y: [0, -8, 0] }}
                            transition={reduce ? undefined : spring}
                        />
                        {/* intermediate plate */}
                        <motion.span
                            className="absolute inset-0 rounded-[18px]"
                            style={{
                                background: "color-mix(in srgb, var(--pg-accent) 13%, var(--pg-surface-2))",
                                border: "1px solid var(--pg-border-strong)",
                            }}
                            animate={reduce ? { z: -7, y: 0 } : { z: [-7, -26, -7], y: [0, -4, 0] }}
                            transition={reduce ? undefined : { ...spring, delay: 0.18 }}
                        />
                        {/* lit face — eases forward as the chip opens up */}
                        <motion.span
                            className="absolute inset-0 flex items-center justify-center rounded-[18px]"
                            style={{
                                background: "linear-gradient(150deg, var(--pg-surface-2) 0%, var(--pg-surface) 100%)",
                                border: "1px solid var(--pg-border-strong)",
                                boxShadow:
                                    "inset 0 1px 0 color-mix(in srgb, #ffffff 16%, transparent), 0 20px 42px -18px rgba(0,0,0,0.6)",
                            }}
                            animate={reduce ? { z: 0 } : { z: [0, 18, 0] }}
                            transition={reduce ? undefined : spring}
                        >
                            <Icon className="h-7 w-7 sm:h-8 sm:w-8" style={{ color: "var(--pg-accent)" }} strokeWidth={1.4} />
                        </motion.span>
                    </motion.div>
                </div>
            </motion.div>
        </div>
    );
};

const TRACKS = [
    {
        kind: "logo",
        variant: "navy",
        iconColor: "#5C86FF",
        title: "Logo Systems",
        desc: "Focused identity marks with the essentials to launch, crafted to feel considered from day one.",
        points: ["Primary logo and variations", "Colour and type foundations", "Usage-ready delivery files"],
    },
    {
        kind: "branding",
        variant: "nebula",
        iconColor: "#C39BFF",
        title: "Full Brand Ecosystems",
        desc: "End-to-end identity systems: strategy, visual language, and the assets to run a brand everywhere.",
        points: ["Strategy and positioning", "Complete visual identity", "Brand guidelines and assets"],
    },
];

/**
 * ServicesPreview — two-track teaser (logo vs full branding) with a live
 * "from" price pulled from the catalog, routing into the full /services page.
 *
 * Each package is now a branded, dimensional surface: a slow moving colour
 * gradient (navy blue for Logo Systems, nebula purple for Full Brand
 * Ecosystems) with a soft light line travelling clockwise around the edge, so
 * the cards pop out of the dark canvas as distinct "worlds". The CTA is a soft
 * golden glass button with its own clockwise yellow edge-light.
 */
export const ServicesPreview = () => {
    const { packages } = useCatalog();
    const reduce = useReducedMotion();
    const minPrice = (kind) => {
        const arr = (packages || [])
            .filter((p) => p.kind === kind && p.visible !== false && Number(p.base_price) > 0)
            .map((p) => Number(p.base_price));
        return arr.length ? Math.min(...arr) : null;
    };

    return (
        <section id="services" className="pg-section pg-surface-teal" data-testid="home-services-preview" aria-label="Services">
            <SectionGlow tone="warm" />
            <div className="pg-container">
                <Reveal className="max-w-2xl">
                    <span className="pg-eyebrow">Services · Two Tracks</span>
                    <h2 className="font-display mt-5 text-[clamp(2rem,4.6vw,3.2rem)] leading-[1.06]" style={{ color: "var(--pg-fg)" }}>
                        Pick the level of <span className="pg-text-gold-static">ambition</span> for your brand.
                    </h2>
                </Reveal>

                <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
                    {TRACKS.map((t, i) => {
                        const from = minPrice(t.kind);
                        return (
                            <Reveal key={t.kind} delay={0.06 * i}>
                                <div
                                    className={`pg-pack pg-pack--${t.variant} relative flex h-full flex-col overflow-hidden rounded-[22px] p-7 sm:p-8`}
                                    data-reduce={reduce ? "true" : "false"}
                                >
                                    <span className="pg-pack__light pg-pack__light--a" aria-hidden="true" />
                                    <span className="pg-pack__light pg-pack__light--b" aria-hidden="true" />
                                    <Icon3D kind={t.kind} reduce={reduce} accent={t.iconColor} />
                                    <div className="relative z-[2] flex h-full flex-col">
                                        <div className="pr-20 sm:pr-24">
                                            <h3 className="pg-pack__title font-display text-2xl sm:text-3xl">{t.title}</h3>
                                            {from != null && (
                                                <div className="pg-pack__from mt-2 font-mono text-[11px] uppercase tracking-[0.2em]">
                                                    From {formatUSD(from)}
                                                </div>
                                            )}
                                        </div>
                                        <p className="pg-pack__desc mt-4 text-base leading-relaxed">{t.desc}</p>
                                        <ul className="mt-6 space-y-3">
                                            {t.points.map((p) => (
                                                <li key={p} className="pg-pack__point flex items-start gap-3 text-sm">
                                                    <span className="pg-pack__check mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full">
                                                        <Check className="h-3 w-3" strokeWidth={2} />
                                                    </span>
                                                    {p}
                                                </li>
                                            ))}
                                        </ul>
                                        <Link
                                            to="/services"
                                            data-testid={`home-services-${t.kind}-cta`}
                                            className="pg-btn-ghost pg-btn-glass-lume-gold mt-8 inline-flex self-start"
                                        >
                                            <span>Explore packages</span>
                                            <ArrowRight className="h-4 w-4" strokeWidth={1.6} />
                                        </Link>
                                    </div>
                                    <span className="pg-pack__ring" aria-hidden="true" />
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default ServicesPreview;
